import type { IValidationError } from '@wyz/types';
import { DISCOUNT_TYPE } from '~/common/translators';
import { PRICING_TYPE_ENUM } from '~/purchase/schemas';
import {
  IDistributionExtraBonusDraft,
  IDistributionPricingPolicyBrand,
  IDistributionPricingPolicySeason,
  IPricingConfigurationType,
  ISaveDistributionPricingPolicyBrandPayload,
} from '~/purchase/types';

const DEFAULT_CURRENCY_SIGN = '€';

export const NO_VALUE = '-';

/**
 * Catalog season ids are stable, so they drive the colour band. An unknown id simply gets no tint.
 */
const SEASON_MODIFIERS: Record<number, string> = {
  1: 'summer',
  2: 'winter',
  3: 'all-season',
};

export function seasonClass(season: IDistributionPricingPolicySeason): string {
  const modifier = SEASON_MODIFIERS[season.id];

  return modifier ? ` extra-bonus-table__season--${modifier}` : '';
}

export function tintClass(season: IDistributionPricingPolicySeason): string {
  const modifier = SEASON_MODIFIERS[season.id];

  return modifier ? ` extra-bonus-table__tint--${modifier}` : '';
}

export function tooltipClass(season: IDistributionPricingPolicySeason): string {
  const modifier = SEASON_MODIFIERS[season.id];

  return modifier ? ` extra-bonus-tooltip--${modifier}` : '';
}

export const EMPTY_DRAFT: IDistributionExtraBonusDraft = {
  bonusType: PRICING_TYPE_ENUM.DISCOUNT_BF,
  hqBonus: null,
  values: {},
};

/**
 * The total is only derivable for a BF discount, on both sides: the purchase amount has to be a BF
 * discount for the percentage to apply to it, and the bonus itself has to be a Remise BF — a margin
 * or a net margin expresses something the column cannot show.
 *
 * The HQ bonus of the brand and the extra bonus of the cell stack, so the discount applied to the
 * purchase amount is their sum. Either one alone is enough for a total; only a cell without any
 * bonus at all has none.
 */
export function computeTotal(
  purchaseValue: number,
  discountType: number,
  extraBonus: number | null,
  bonusType: IPricingConfigurationType | null,
  hqBonus: number | null = null,
): number | null {
  if (
    bonusType !== PRICING_TYPE_ENUM.DISCOUNT_BF ||
    discountType !== DISCOUNT_TYPE.BF ||
    (null === extraBonus && null === hqBonus)
  ) {
    return null;
  }

  const ratio = ((hqBonus ?? 0) + (extraBonus ?? 0)) / 100;

  return purchaseValue * (1 - ratio);
}

/** Extra bonuses are keyed on a supplier type and a season, whatever the diameters behind them. */
export function cellKey(supplierTypeId: number, seasonId: number): string {
  return `${supplierTypeId}-${seasonId}`;
}

/** A discount and a margin are percentages; a net margin is an amount in the value's currency. */
export function bonusSign(bonusType?: IPricingConfigurationType): string {
  return PRICING_TYPE_ENUM.NET_MARGIN === bonusType
    ? DEFAULT_CURRENCY_SIGN
    : '%';
}

export function toEditionDraft(
  brand: IDistributionPricingPolicyBrand,
): IDistributionExtraBonusDraft {
  const values: IDistributionExtraBonusDraft['values'] = {};

  brand.rows.forEach((row) => {
    Object.entries(row.seasons).forEach(([seasonId, cell]) => {
      values[cellKey(row.supplierTypeId, Number(seasonId))] =
        cell?.extraBonus ?? null;
    });
  });

  return {
    bonusType: brand.bonusType ?? PRICING_TYPE_ENUM.DISCOUNT_BF,
    hqBonus: brand.hqBonus,
    values,
  };
}

/**
 * A cleared cell is simply not sent, so the position of a cell in the payload is not the position of
 * its field in the draft. The kept entries are listed once here, and both the payload and the mapping
 * back from the server property paths read that same list.
 */
export function payloadEntries(
  draft: IDistributionExtraBonusDraft,
): [string, number][] {
  return Object.entries(draft.values ?? {}).filter(
    (entry): entry is [string, number] =>
      null !== entry[1] && undefined !== entry[1] && !Number.isNaN(entry[1]),
  );
}

/** A cleared cell is simply not sent: the whole brand travels in one save. */
export function toSaveBrandPayload(
  brand: string,
  draft: IDistributionExtraBonusDraft,
): ISaveDistributionPricingPolicyBrandPayload {
  return {
    brand,
    bonusType: draft.bonusType,
    hqBonus: draft.hqBonus ?? null,
    values: payloadEntries(draft).map(([key, value]) => {
      const [supplierTypeId, seasonId] = key.split('-');

      return {
        supplierTypeId: Number(supplierTypeId),
        seasonId: Number(seasonId),
        value,
      };
    }),
  };
}

const VALUE_PROPERTY_PATH = /^values\[(\d+)\](?:\.value)?$/;

/**
 * The server validates the payload, whose values are an array, while the fields are keyed on a
 * supplier type and a season. A violation on `values[0].value` is therefore moved back onto the field
 * that produced that entry. A path pointing at nothing on screen — the brand, or an index the draft no
 * longer holds — is dropped: the generic error toast already reports the failure.
 */
export function toExtraBonusFormErrors<TError extends IValidationError>(
  errors: TError[],
  draft: IDistributionExtraBonusDraft,
): TError[] {
  const keys = payloadEntries(draft).map(([key]) => key);

  return errors.reduce<TError[]>((mapped, error) => {
    if (
      'hqBonus' === error.propertyPath ||
      'bonusType' === error.propertyPath
    ) {
      mapped.push(error);

      return mapped;
    }

    const [, index] = VALUE_PROPERTY_PATH.exec(error.propertyPath) ?? [];
    const key = undefined === index ? undefined : keys[Number(index)];

    if (undefined !== key) {
      mapped.push({ ...error, propertyPath: `values.${key}` });
    }

    return mapped;
  }, []);
}

/**
 * A divergent cell is summarised by the range its combinations span, which stays readable whatever
 * the number of diameters. The unit only belongs to the summary when they all share the same
 * discount type, so a divergence there returns no discount type at all.
 */
export function summarizeRange(
  values: number[],
  discountTypes: number[],
): { text: string; discountType: number | null } | null {
  if (0 === values.length) {
    return null;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);
  const [discountType] = discountTypes;
  const isSameDiscountType = discountTypes.every(
    (type) => type === discountType,
  );

  return {
    text: min === max ? String(min) : `${min} – ${max}`,
    discountType: isSameDiscountType ? discountType : null,
  };
}
